import { useMutation, useQuery } from "@tanstack/react-query";
import { APIErrorResponse } from "src/types/generic";
import apiClient from "../apiClient";

interface IBody {
  id?: string;
  branch_id: number;
  quantity: number;
  item_id: number;
  items: {
    quantity: number;
    item_id: number;
  }[];
}

interface IProTransBody {
  branch_id?: number;
  item_id: number;
  quantity: number;
  total_cost: number;
  date: string;
}

interface IParams {
  page?: number;
}

export const useGetProRecipeById = ({
  id,
  enabled,
}: {
  id: string | number;
  enabled: boolean;
}) =>
  useQuery<any, APIErrorResponse, any>({
    queryKey: ["get-pro-recipe-by-id", id],
    queryFn: async () => {
      return apiClient.get(`/api/productRecipes/${id}`);
    },
    gcTime: 0,
    enabled,
  });

export const useAddProductionRecipeMutation = () =>
  useMutation<void, APIErrorResponse, IBody>({
    mutationFn: async (data) => {
      return apiClient.post(`/api/productRecipes`, data);
    },
  });

export const useUpdateProductionRecipeMutation = () =>
  useMutation<void, APIErrorResponse, IBody>({
    mutationFn: async (data) => {
      const { id, ...rest } = data;
      return apiClient.put(`/api/productRecipes/${id}`, rest);
    },
  });

export const useAddProductionTransMutation = () =>
  useMutation<any, APIErrorResponse, IProTransBody>({
    mutationFn: async (data) => {
      return apiClient.post(`/api/productPros`, data);
    },
  });

export const useGetProEntryList = (params: IParams) =>
  useQuery<any, APIErrorResponse, any>({
    queryKey: ["get-pro-entries-list", params.page],
    queryFn: async () => {
      return apiClient.get(`/api/productPros`, { params });
    },
  });

export const useDownloadProductionEntries = () =>
  useQuery<any, APIErrorResponse, any>({
    queryKey: ["download-production-entries"],
    queryFn: async () => {
      return apiClient.get(`/api/productPros/download`);
    },
    enabled: false,
    gcTime: 0,
  });

export const useGetProEntryById = ({
  id,
  enabled,
}: {
  id: string;
  enabled: boolean;
}) =>
  useQuery<any, APIErrorResponse, any>({
    queryKey: ["get-pro-entry-by-id", id],
    queryFn: async () => {
      return apiClient.get(`/api/productPros/${id}`);
    },
    gcTime: 0,
    enabled,
  });

export const useDeleteProductionEntryMutation = () =>
  useMutation<void, APIErrorResponse, number>({
    mutationFn: async (id) => {
      return apiClient.delete(`/api/productPros/${id}`);
    },
  });

interface IProParams {
  name?: string;
  sku?: string;
  method?: string; // direct and indirect
}

export const useDownloadProductionRecipes = (params: IProParams) =>
  useQuery<any, APIErrorResponse, any>({
    queryKey: [
      "download-production-recipes",
      params.name,
      params.sku,
      params.method,
    ],
    queryFn: async () => {
      return apiClient.get(`/api/productRecipes/download`, { params });
    },
    enabled: false,
    gcTime: 0,
  });
